/**
 * Application router — React Router v7.
 *
 * Layout routes:
 *   /               → RootRedirect (auth guard)
 *   auth/*          → AuthLayout (split-screen, initSecurityChecks)
 *   survey          → OnboardingLayout
 *   (dashboard)     → DashboardLayout (sidebar shell, auth guard inside)
 *   profile         → ProfileLayout (4th layout — settings pages; their nav renders in the main sidebar)
 *   security-warning → standalone
 *   *               → NotFound
 *
 * Full-bleed dashboard routes: DashboardLayout reads route handle.fullBleed via useMatches().
 */

import { Suspense } from "react";
import { lazyWithReload } from "@/lib/utils/lazy-with-reload";
import type { RouteObject } from "react-router-dom";
import { Navigate, createBrowserRouter, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { RootProviders } from "@/app/root-providers";
import { ClaimOfferLayout } from "@/features/special-offers/claim-offer-layout";
import { I18nProvider } from "@/providers/i18n-provider";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { AuthLayout } from "@/components/layout/auth-layout";
import { OnboardingLayout } from "@/components/layout/onboarding-layout";
import { ProfileLayout } from "@/components/layout/profile-layout";
import { RootRedirect } from "@/components/layout/root-redirect";
import { VoiceAgentRouteGuard } from "@/app/routes/voice-agent-route-guard";
import { KnowledgeBaseRouteGuard } from "@/app/routes/knowledge-base-route-guard";
import { NotFound } from "@/components/shared/not-found";
import { RouteError } from "@/components/shared/route-error";
import { DashboardError } from "@/components/shared/dashboard-error";
import { RootError } from "@/components/shared/root-error";
import { PageContentSkeleton } from "@/components/shared/page-content-skeleton";
import { MediaInsightSkeleton } from "@/features/media-insight/components/media-insight-skeleton";
import { PricingPage as _PricingPage } from "@/features/pricing/pricing-page";
import { UploadPage as _UploadPage } from "@/features/upload/upload-page";
import { UploadRecordPage as _UploadRecordPage } from "@/features/upload/upload-record-page";

// -- Suspense fallbacks -------------------------------------------------------

function AuthLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
      <span className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" aria-hidden="true" />
    </div>
  );
}

type LazyPage = React.LazyExoticComponent<() => React.ReactElement>;

function Lazy({ element: El, fallback }: { element: LazyPage; fallback?: React.ReactNode }) {
  return <Suspense fallback={fallback ?? <PageContentSkeleton />}>{<El />}</Suspense>;
}

function LazyAuth({ element: El }: { element: LazyPage }) {
  return <Suspense fallback={<AuthLoader />}><El /></Suspense>;
}

function LegacyPathRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

// -- Lazy page imports -------------------------------------------------------

// Auth
const LoginPage = lazyWithReload(() => import("@/features/auth/login-page"), "LoginPage");
const RegisterPage = lazyWithReload(() => import("@/features/auth/register-page"), "RegisterPage");
const ForgotPasswordPage = lazyWithReload(() => import("@/features/auth/forgot-password-page"), "ForgotPasswordPage");
const ChangePasswordPage = lazyWithReload(() => import("@/features/auth/change-password-page"), "ChangePasswordPage");
const VerifyPage = lazyWithReload(() => import("@/features/auth/verify-page"), "Verify2FAPage");
const VerifyEmailPage = lazyWithReload(() => import("@/features/auth/verify-email-page"), "VerifyEmailPage");
const VerifyOtpPage = lazyWithReload(() => import("@/features/auth/verify-otp-page"), "VerifyOTPPage");
const VerifyUserPage = lazyWithReload(() => import("@/features/auth/verify-user-page"), "VerifyUserPage");
const OAuthCallbackPage = lazyWithReload(() => import("@/features/auth/oauth-callback-page"), "OAuthCallbackPage");
const DesktopAuthPage = lazyWithReload(() => import("@/features/auth/desktop-auth-page"), "DesktopAuthPage");
const OAuthResumePage = lazyWithReload(() => import("@/features/auth/oauth-resume-page"), "OAuthResumePage");
const InvitePage = lazyWithReload(() => import("@/features/auth/invite-page"), "TeamInvitePage");

// Dashboard
const DashboardPage = lazyWithReload(() => import("@/features/dashboard/dashboard-page"), "DashboardPage");
const DashboardsPage = lazyWithReload(() => import("@/features/dashboards/dashboards-page"), "DashboardsPage");
const ExplorePage = lazyWithReload(() => import("@/features/explore/explore-page"), "ExplorePage");
const FavoritesPage = lazyWithReload(() => import("@/features/favorites/favorites-page"), "FavoritesPage");
const FolderListPage = lazyWithReload(() => import("@/features/folder/folder-list-page"), "FolderListPage");
const FolderDetailPage = lazyWithReload(() => import("@/features/folder/folder-detail-page"), "FolderDetailPage");
const ClipsPage = lazyWithReload(() => import("@/features/clips/clips-page"), "ClipsPage");
const AutomationsPage = lazyWithReload(() => import("@/features/automations/automations-page"), "AutomationsPage");
const AutomationCanvasPage = lazyWithReload(() => import("@/features/automations/automation-canvas"), "AutomationCanvasPage");
const AutomationRunsPage = lazyWithReload(() => import("@/features/automations/automation-runs-page"), "AutomationRunsPage");
const ChatHistoryPage = lazyWithReload(() => import("@/features/chat/chat-page"), "ChatPage");
const TranslatePage = lazyWithReload(() => import("@/features/translate/translate-page"), "TranslatePage");
const SurveysPage = lazyWithReload(() => import("@/features/surveys/surveys-page"), "SurveysPage");
const TeamPage = lazyWithReload(() => import("@/features/team/team-page"), "TeamPage");
const NotificationsPage = lazyWithReload(() => import("@/features/notifications/notifications-page"), "NotificationsPage");
const MeetingAssistantPage = lazyWithReload(() => import("@/features/meeting-assistant/meeting-assistant-page"), "MeetingAssistantPage");
const SpecialOffersPage = lazyWithReload(() => import("@/features/special-offers/special-offers-page"), "SpecialOffersPage");
const ClaimOfferSuccessPage = lazyWithReload(() => import("@/features/special-offers/claim-offer-success-page"), "ClaimOfferSuccessPage");
const ChangelogPage = lazyWithReload(() => import("@/features/changelog/changelog-page"), "ChangelogPage");

// Media
const MediaEditorPage = lazyWithReload(() => import("@/features/media/media-editor-page"), "MediaEditorPage");
const MediaInsightRoute = lazyWithReload(() => import("@/app/routes/media-insight-route"), "MediaInsightRoute");
const MediaDeeplinkRoute = lazyWithReload(() => import("@/app/routes/media-deeplink-route"), "MediaDeeplinkRoute");

// Developers
const DevelopersPage = lazyWithReload(() => import("@/features/developers/developers-page"), "DevelopersPage");
const WebhookDetailPage = lazyWithReload(() => import("@/features/developers/webhook-detail-page"), "WebhookDetailPage");

// Integrations
const IntegrationsPage = lazyWithReload(() => import("@/features/integrations/integrations-page"), "IntegrationsPage");
// Standalone (no auth guard) — popup has no live session; mounted outside DashboardLayout to bypass the auth gate.
const OAuthCompletePage = lazyWithReload(() => import("@/features/integrations/oauth-complete-page"), "OAuthCompletePage");
const SlackPage = lazyWithReload(() => import("@/features/integrations/slack-page"), "SlackIntegrationPage");
const CalendarPage = lazyWithReload(() => import("@/features/integrations/calendar-page"), "CalendarIntegrationPage");
const VimeoPage = lazyWithReload(() => import("@/features/integrations/vimeo-page"), "VimeoIntegrationPage");
const ZapierPage = lazyWithReload(() => import("@/features/integrations/zapier-page"), "ZapierIntegrationPage");
const WebscraperPage = lazyWithReload(() => import("@/features/integrations/webscraper/webscraper-page"), "WebScraperIntegrationPage");
const LarkPage = lazyWithReload(() => import("@/features/integrations/lark/lark-page"), "LarkIntegrationPage");
const GenesysPage = lazyWithReload(() => import("@/features/integrations/genesys/genesys-page"), "GenesysIntegrationPage");
const CsvImportPage = lazyWithReload(() => import("@/features/csv-import/csv-import-page"), "CsvImportPage");

// Recorder
const RecorderListPage = lazyWithReload(() => import("@/features/recorder/recorder-list-page"), "RecorderListPage");
const RecorderCreatePage = lazyWithReload(() => import("@/features/recorder/recorder-create-page"), "RecorderCreatePage");
const RecorderDetailPage = lazyWithReload(() => import("@/features/recorder/recorder-detail-page"), "RecorderDetailPage");

// Embed
const EmbedPage = lazyWithReload(() => import("@/features/embed/embed-page"), "EmbedPage");
const EmbedAnalyticsPage = lazyWithReload(() => import("@/features/embed/embed-analytics-page"), "EmbedAnalyticsPage");

// Speak Agents (nav shell — placeholder pages)
const AgentsHomePage = lazyWithReload(() => import("@/features/voice-agents/agents-home-page"), "AgentsHomePage");
const AgentsListPage = lazyWithReload(() => import("@/features/voice-agents/agents-list-page"), "AgentsListPage");
const AgentOverviewPage = lazyWithReload(() => import("@/features/voice-agents/agent-overview-page"), "AgentOverviewPage");
const AgentsEditorPage = lazyWithReload(() => import("@/features/voice-agents/agents-editor-page"), "AgentsEditorPage");
const AgentSharePage = lazyWithReload(() => import("@/features/voice-agents/agent-share-page"), "AgentSharePage");
const AgentsConversationsPage = lazyWithReload(() => import("@/features/voice-agents/conversations-page"), "ConversationsPage");
const AgentsPhoneNumbersPage = lazyWithReload(() => import("@/features/voice-agents/phone-numbers-page"), "PhoneNumbersPage");
const KnowledgeBasePage = lazyWithReload(() => import("@/features/knowledge-base/knowledge-base-page"), "KnowledgeBasePage");
const KnowledgeCollectionPage = lazyWithReload(() => import("@/features/knowledge-base/knowledge-collection-page"), "KnowledgeCollectionPage");

// Profile
const ProfilePage = lazyWithReload(() => import("@/features/profile/profile-page"), "ProfileOverviewPage");
const SecurityPage = lazyWithReload(() => import("@/features/profile/security-page"), "SecurityPage");
const UsagePage = lazyWithReload(() => import("@/features/profile/usage-page"), "UsagePage");
const PaymentPage = lazyWithReload(() => import("@/features/profile/payment-page"), "PaymentSettingsPage");
const NotificationSettingsPage = lazyWithReload(() => import("@/features/profile/notification-settings-page"), "NotificationSettingsPage");
const PreferencesPage = lazyWithReload(() => import("@/features/profile/preferences-page"), "PreferencesPage");
const CustomizationPage = lazyWithReload(() => import("@/features/profile/customization-page"), "CustomizationPage");
const DomainsPage = lazyWithReload(() => import("@/features/profile/domains-page"), "DomainsPage");
const VocabularyPage = lazyWithReload(() => import("@/features/profile/vocabulary-page"), "VocabularyPage");
const FieldsPage = lazyWithReload(() => import("@/features/profile/fields-page"), "FieldsPage");
const LabelsPage = lazyWithReload(() => import("@/features/labels/labels-page"), "LabelsPage");
const DataPage = lazyWithReload(() => import("@/features/profile/data-page"), "DataManagementPage");
const AssistantTemplatesPage = lazyWithReload(() => import("@/features/profile/ai-assistant-page"), "AssistantTemplatesPage");

// Onboarding
const SurveyPage = lazyWithReload(() => import("@/features/onboarding/survey-page"), "SurveyPage");

// Security Warning
const SecurityWarningPage = lazyWithReload(() => import("@/app/security-warning-page"), "SecurityWarningPage");

// -- Router ------------------------------------------------------------------

const createSentryRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// Old/renamed routes and email links that must redirect instead of 404ing.
const legacyRedirects: { from: string; to: string }[] = [
  { from: "profile/payment-information", to: "/profile/payment" },
  { from: "developers/webhooks", to: "/developers" },
  { from: "settings/team", to: "/team" },
];

export const routes: RouteObject[] = [
  {
    // Public offer-claim routes — mounted outside RootProviders so logged-out
    // visitors aren't bounced to /auth/login by AuthProvider's session guard.
    element: <ClaimOfferLayout />,
    errorElement: <RootError />,
    children: [
      { path: "claim-offer", element: <Lazy element={SpecialOffersPage} /> },
      {
        path: "claim-offer/success",
        element: <Lazy element={ClaimOfferSuccessPage} />,
      },
    ],
  },
  {
    // OAuth-complete landing — OUTSIDE RootProviders so the popup (which has no
    // live session) is not bounced to /auth/login by AuthProvider's guard.
    // Standalone: reads query params, postMessages the opener, closes.
    path: "integrations/oauth-complete",
    element: (
      <I18nProvider>
        <Suspense fallback={<PageContentSkeleton />}>
          <OAuthCompletePage />
        </Suspense>
      </I18nProvider>
    ),
    errorElement: <RootError />,
  },
  {
    // Root layout route — hosts router-context providers (Auth, ThemeApplier,
    // Experiment, I18n, ErrorBoundary). All routes are children so they render
    // inside the providers.
    element: <RootProviders />,
    errorElement: <RootError />,
    children: [
      // Root redirect — uses useAuth().isLoggedIn (httpOnly cookie not readable)
      { index: true, element: <RootRedirect /> },

      // Public standalone page
      {
        path: "security-warning",
        element: <Suspense fallback={<PageContentSkeleton />}><SecurityWarningPage /></Suspense>,
      },

      // Desktop handoff — outside AuthLayout, which bounces signed-in users off /auth/*
      { path: "desktop-auth", element: <LazyAuth element={DesktopAuthPage} /> },

      { path: "oauth/resume", element: <LazyAuth element={OAuthResumePage} /> },

      // Legacy auth paths — inbound links (marketing site, old emails, external
      // references) point at bare /register and /login, which fell through to the
      // 404 catch-all. LegacyPathRedirect preserves search params so UTM and click-id
      // attribution survives the hop. See PR #3942.
      { path: "register", element: <LegacyPathRedirect to="/auth/register" /> },
      { path: "login", element: <LegacyPathRedirect to="/auth/login" /> },

      // Auth routes — AuthLayout has initSecurityChecks + split-screen branding
      {
        path: "auth",
        element: <AuthLayout />,
        errorElement: <RouteError />,
        children: [
          { path: "login", element: <LazyAuth element={LoginPage} /> },
          { path: "register", element: <LazyAuth element={RegisterPage} /> },
          { path: "forgot-password", element: <LazyAuth element={ForgotPasswordPage} /> },
          { path: "change-password", element: <LazyAuth element={ChangePasswordPage} /> },
          {
            path: "verify",
            children: [
              { index: true, element: <LazyAuth element={VerifyPage} /> },
              { path: "email", element: <LazyAuth element={VerifyEmailPage} /> },
              { path: "otp", element: <LazyAuth element={VerifyOtpPage} /> },
            ],
          },
          { path: "verify-user", element: <LazyAuth element={VerifyUserPage} /> },
          { path: "oauth-callback", element: <LazyAuth element={OAuthCallbackPage} /> },
          { path: "invite/:slug/:token", element: <LazyAuth element={InvitePage} /> },
        ],
      },

      // Onboarding
      {
        path: "survey",
        element: <OnboardingLayout />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Lazy element={SurveyPage} fallback={<PageContentSkeleton variant="form" />} /> },
        ],
      },

      // Dashboard — auth guard inside DashboardLayout; full-bleed via route handle
      {
        element: <DashboardLayout />,
        errorElement: <RouteError />,
        children: [
          // Pathless error boundary: errors render in content area, sidebar stays alive
          {
            errorElement: <DashboardError />,
            children: [
              { path: "home", element: <Lazy element={DashboardPage} fallback={<PageContentSkeleton variant="grid" />} /> },
              { path: "dashboard", element: <Navigate to="/home" replace /> },
              { path: "dashboards", element: <Lazy element={DashboardsPage} fallback={<PageContentSkeleton variant="grid" />} /> },
              { path: "dashboards/:dashboardId", element: <Lazy element={DashboardsPage} fallback={<PageContentSkeleton variant="grid" />} /> },
              { path: "explore", element: <Lazy element={ExplorePage} fallback={<PageContentSkeleton variant="grid" />} /> },
              { path: "favorites", element: <Lazy element={FavoritesPage} fallback={<PageContentSkeleton variant="grid" />} /> },
              { path: "folder", element: <Lazy element={FolderListPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "folder/:id", element: <Lazy element={FolderDetailPage} fallback={<div className="px-4 sm:px-6 md:px-8 pt-6"><PageContentSkeleton variant="table" /></div>} />, handle: { fullBleed: true } },
              { path: "folder/:id/insight/:mediaId", element: <Suspense fallback={<MediaInsightSkeleton />}><MediaInsightRoute /></Suspense>, handle: { fullBleed: true } },
              { path: "clips", element: <Lazy element={ClipsPage} fallback={<PageContentSkeleton variant="grid" />} /> },
              { path: "automations", element: <Lazy element={AutomationsPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "automations/new", element: <Lazy element={AutomationCanvasPage} fallback={<PageContentSkeleton variant="form" />} />, handle: { fullBleed: true } },
              { path: "automations/:automationId/edit", element: <Lazy element={AutomationCanvasPage} fallback={<PageContentSkeleton variant="form" />} />, handle: { fullBleed: true } },
              { path: "automations/:automationId/runs", element: <Lazy element={AutomationRunsPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "chat", element: <Navigate to="history" replace /> },
              { path: "chat/history", element: <Lazy element={ChatHistoryPage} fallback={<div className="px-4 sm:px-6 md:px-8 pt-6"><PageContentSkeleton variant="form" /></div>} />, handle: { fullBleed: true } },
              { path: "translate", element: <Lazy element={TranslatePage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "surveys", element: <Lazy element={SurveysPage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "team", element: <Lazy element={TeamPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "notifications", element: <Lazy element={NotificationsPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "changelog", element: <Lazy element={ChangelogPage} /> },
              { path: "pricing", element: <Suspense fallback={<PageContentSkeleton variant="grid" />}><_PricingPage /></Suspense> },
              { path: "special-offers", element: <Lazy element={SpecialOffersPage} /> },
              { path: "meeting-assistant", element: <Lazy element={MeetingAssistantPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "developers", element: <Lazy element={DevelopersPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "developers/webhooks/:id", element: <Lazy element={WebhookDetailPage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "developers/apikeys", element: <LegacyPathRedirect to="/developers#api-keys" /> },
              { path: "integrations", element: <Lazy element={IntegrationsPage} fallback={<PageContentSkeleton variant="grid" columns={3} rows={2} />} /> },
              {
                element: <KnowledgeBaseRouteGuard />,
                children: [
                  { path: "knowledge-base", element: <Lazy element={KnowledgeBasePage} fallback={<PageContentSkeleton variant="table" />} /> },
                  { path: "knowledge-base/:kbId", element: <Lazy element={KnowledgeCollectionPage} fallback={<PageContentSkeleton variant="form" />} /> },
                ],
              },
              { path: "integrations/slack", element: <Lazy element={SlackPage} fallback={<PageContentSkeleton variant="form" count={2} />} /> },
              { path: "integrations/calendar/:type", element: <Lazy element={CalendarPage} fallback={<PageContentSkeleton variant="form" count={2} />} /> },
              { path: "integrations/vimeo", element: <Lazy element={VimeoPage} fallback={<PageContentSkeleton variant="form" count={2} />} /> },
              { path: "integrations/zapier", element: <Lazy element={ZapierPage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "integrations/webscraper", element: <Lazy element={WebscraperPage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "integrations/lark", element: <Lazy element={LarkPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "integrations/genesys", element: <Lazy element={GenesysPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "integrations/csv", element: <Lazy element={CsvImportPage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "media/editor/:id", element: <Lazy element={MediaEditorPage} fallback={<MediaInsightSkeleton />} />, handle: { fullBleed: true } },
              { path: "media/:id", element: <Suspense fallback={<MediaInsightSkeleton />}><MediaDeeplinkRoute /></Suspense> },
              { path: "recorder", element: <Lazy element={RecorderListPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "recorder/create", element: <Lazy element={RecorderCreatePage} fallback={<PageContentSkeleton variant="form" />} /> },
              { path: "recorder/:id", element: <Lazy element={RecorderDetailPage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },
              { path: "upload", element: <Suspense fallback={<PageContentSkeleton variant="form" />}><_UploadPage /></Suspense> },
              { path: "upload/record", element: <Suspense fallback={<PageContentSkeleton variant="form" />}><_UploadRecordPage /></Suspense> },
              { path: "embed-media", element: <Lazy element={EmbedPage} fallback={<PageContentSkeleton variant="table" />} /> },
              { path: "embed/:embedId/analytics", element: <Lazy element={EmbedAnalyticsPage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },

              // Speak Agents — gated behind the voiceAgent.enabled feature (nav + routes)
              {
                element: <VoiceAgentRouteGuard />,
                children: [
                  { path: "agents", element: <Lazy element={AgentsHomePage} fallback={<PageContentSkeleton variant="grid" />} /> },
                  { path: "agents/list", element: <Lazy element={AgentsListPage} fallback={<PageContentSkeleton variant="grid" />} /> },
                  { path: "agents/create", element: <Lazy element={AgentsEditorPage} fallback={<PageContentSkeleton variant="grid" />} /> },
                  { path: "agents/list/new", element: <Navigate to="/agents/create" replace /> },
                  { path: "agents/list/:agentId", element: <Lazy element={AgentOverviewPage} fallback={<PageContentSkeleton variant="grid" />} /> },
                  { path: "agents/list/:agentId/edit", element: <Lazy element={AgentsEditorPage} fallback={<PageContentSkeleton variant="grid" />} /> },
                  { path: "agents/list/:agentId/share", element: <Lazy element={AgentSharePage} fallback={<PageContentSkeleton variant="form" />} /> },
                  { path: "agents/conversations", element: <Lazy element={AgentsConversationsPage} fallback={<PageContentSkeleton variant="table" />} /> },
                  { path: "agents/phone-numbers", element: <Lazy element={AgentsPhoneNumbersPage} fallback={<PageContentSkeleton variant="table" />} /> },
                  { path: "agents/integrations", element: <Lazy element={IntegrationsPage} fallback={<PageContentSkeleton variant="grid" columns={3} rows={2} />} /> },
                ],
              },
              ...legacyRedirects.map(({ from, to }) => ({
                path: from,
                element: <LegacyPathRedirect to={to} />,
              })),

              // Profile layout (4th layout — permission-filtered settings nav)
              {
                path: "profile",
                element: <ProfileLayout />,
                children: [
                  { index: true, element: <Lazy element={ProfilePage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },
                  { path: "security", element: <Lazy element={SecurityPage} fallback={<PageContentSkeleton variant="form" count={2} />} /> },
                  { path: "usage", element: <Lazy element={UsagePage} fallback={<PageContentSkeleton variant="stats" />} /> },
                  { path: "payment", element: <Lazy element={PaymentPage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },
                  { path: "notifications", element: <Lazy element={NotificationSettingsPage} fallback={<PageContentSkeleton variant="form" count={4} />} /> },
                  { path: "preferences", element: <Lazy element={PreferencesPage} fallback={<PageContentSkeleton variant="form" count={4} />} /> },
                  { path: "navigation", element: <Navigate to="/profile/preferences" replace /> },
                  { path: "customization", element: <Lazy element={CustomizationPage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },
                  { path: "domains", element: <Lazy element={DomainsPage} fallback={<PageContentSkeleton variant="form" count={2} />} /> },
                  { path: "vocabulary", element: <Lazy element={VocabularyPage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },
                  { path: "fields", element: <Lazy element={FieldsPage} fallback={<PageContentSkeleton variant="table" />} /> },
                  { path: "labels", element: <Lazy element={LabelsPage} fallback={<PageContentSkeleton variant="table" />} /> },
                  { path: "data", element: <Lazy element={DataPage} fallback={<PageContentSkeleton variant="form" count={3} />} /> },
                  { path: "ai-assistant", element: <Lazy element={AssistantTemplatesPage} fallback={<PageContentSkeleton variant="form" count={2} />} /> },

                  // Legacy redirects — preserve search params
                  { path: "personal", element: <LegacyPathRedirect to="/profile" /> },
                  { path: "password", element: <LegacyPathRedirect to="/profile/security" /> },
                  { path: "appearance", element: <LegacyPathRedirect to="/profile/preferences" /> },
                  { path: "meeting-preferences", element: <LegacyPathRedirect to="/integrations" /> },
                  { path: "manage-accounts", element: <LegacyPathRedirect to="/integrations" /> },
                  { path: "team-management", element: <LegacyPathRedirect to="/team" /> },
                  { path: "email", element: <LegacyPathRedirect to="/profile/notifications" /> },
                ],
              },
            ],
          },
        ],
      },

      // 404
      { path: "*", element: <NotFound /> },
    ],
  },
];

export const router = createSentryRouter(routes);

